if (!customElements.get('sondos-complementary-carousel')) {
  customElements.define('sondos-complementary-carousel', class SondosComplementaryCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-spc-track]');
      this.slides = Array.from(this.querySelectorAll('[data-spc-slide]'));
      this.previousButton = this.querySelector('[data-spc-previous]');
      this.nextButton = this.querySelector('[data-spc-next]');
      this.dots = Array.from(this.querySelectorAll('[data-spc-dot]'));
      this.titleLinks = Array.from(this.querySelectorAll('[data-spc-title-ar]'));
      if (!this.track) return;

      this.activeIndex = 0;
      this.ticking = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.applyTitleFallbacks();
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.applyTitleFallbacks();
      });

      this.resizeObserver = new ResizeObserver(() => {
        this.applyTitleFallbacks();
        if (this.slides.length > 1) this.queueUpdate();
      });
      this.resizeObserver.observe(this.track);

      if (this.slides.length < 2) return;

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
      this.updateState();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    queueUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.updateState();
        this.ticking = false;
      });
    }

    applyTitleFallbacks() {
      this.titleLinks.forEach((link) => {
        const arabicTitle = link.dataset.spcTitleAr?.trim();
        const englishTitle = link.dataset.spcTitleEn?.trim();
        if (!arabicTitle) return;

        const titleBox = link.closest('.spc-card__title');
        const styles = window.getComputedStyle(titleBox);
        const probe = document.createElement('span');
        Object.assign(probe.style, {
          position: 'fixed',
          insetInlineStart: '-9999px',
          insetBlockStart: '0',
          display: 'block',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: `${titleBox.clientWidth}px`,
          fontFamily: styles.fontFamily,
          fontSize: styles.fontSize,
          fontWeight: styles.fontWeight,
          lineHeight: styles.lineHeight,
          letterSpacing: styles.letterSpacing,
          whiteSpace: 'normal',
          wordBreak: styles.wordBreak,
          overflowWrap: styles.overflowWrap,
          direction: 'rtl',
        });
        probe.textContent = arabicTitle;
        document.body.appendChild(probe);

        const lineHeight = Number.parseFloat(styles.lineHeight);
        const measuredHeight = probe.getBoundingClientRect().height;
        const exceedsTwoLines = measuredHeight > lineHeight * 2 + 1;
        const arabicLines = measuredHeight <= lineHeight + 1 ? 1 : measuredHeight <= lineHeight * 2 + 1 ? 2 : 3;
        probe.remove();

        const useEnglish = exceedsTwoLines && englishTitle;
        const useSecondaryEnglish = arabicLines === 1 && englishTitle;

        link.replaceChildren();
        titleBox.classList.toggle('has-secondary', Boolean(useSecondaryEnglish));
        link.classList.toggle('has-secondary', Boolean(useSecondaryEnglish));
        link.classList.toggle('is-english', Boolean(useEnglish));

        if (useSecondaryEnglish) {
          const primary = document.createElement('span');
          primary.className = 'spc-card__title-primary';
          primary.lang = 'ar';
          primary.dir = 'rtl';
          primary.textContent = arabicTitle;

          const secondary = document.createElement('span');
          secondary.className = 'spc-card__title-secondary';
          secondary.lang = 'en';
          secondary.dir = 'ltr';
          secondary.textContent = englishTitle;
          link.append(primary, secondary);
          link.removeAttribute('lang');
          link.removeAttribute('dir');
        } else {
          link.textContent = useEnglish ? englishTitle : arabicTitle;
          link.lang = useEnglish ? 'en' : 'ar';
          link.dir = useEnglish ? 'ltr' : 'rtl';
        }
      });
    }

    updateState() {
      const trackEdge = this.track.getBoundingClientRect().right;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().right - trackEdge);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.activeIndex = closestIndex;
      this.dots.forEach((dot, index) => {
        const isActive = index === closestIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.previousButton) this.previousButton.disabled = closestIndex === 0;
      if (this.nextButton) this.nextButton.disabled = closestIndex === this.slides.length - 1;
    }

    goTo(index) {
      const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides[safeIndex].scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }
  });
}
